import { apiGet, apiPost } from '../../../adapters/api-client/api';

/**
 * PI-3 Epic 2 — settlement / reconciliation / payout-status API client.
 * Mirrors `earnings.ts` shape so screens can share patterns.
 */

export interface HoldbackSlice {
  amount: number;
  inWindow: boolean;
  released: boolean;
  releasedAt?: string;
  windowMsRemaining: number;
}

export interface SettlementBreakdown {
  enrollmentId: string;
  teacherId: string;
  /** What every amount below is denominated in. */
  currency: string;
  courseId: string;
  state: string;
  released: number;
  pending: number;
  holdback: HoldbackSlice;
  refunded: number;
  netEarned: number;
  /** MC-03 — the holdback on the eligible base, refunded sessions excluded. */
  holdbackSettlement?: HoldbackSettlementView;
  /** MC-03 — sessions the teacher did not give, refunded to the family. Never paid or delivered. */
  refundedSessions?: Array<{ sequence: number; amount: number; refundedAt?: string }>;
}

export interface HoldbackSettlementView {
  eligibleBase: number;
  targetHoldback: number;
  paidToDate: number;
  pendingBalance: number;
  releasable: number;
  overpaymentIdentified: number;
  recoveryDue: number;
  excludedSequences: number[];
  status?: 'RELEASED' | 'SETTLED_ZERO' | 'HELD';
  heldReason?: string;
  owner?: string;
  nextAction?: string;
}

export interface SettlementTotals {
  released: number;
  pending: number;
  holdback: number;
  refunded: number;
  netEarned: number;
}

export interface TeacherSettlement {
  teacherId: string;
  /** What the totals are denominated in. */
  currency: string;
  enrollments: SettlementBreakdown[];
  totals: SettlementTotals;
}

export type ReconciliationStatus = 'ok' | 'mismatch' | 'pending';

export interface PayoutOperation {
  operationKey: string;
  action: 'release_booking' | 'release_session' | 'release_holdback' | 'refund';
  enrollmentSide: {
    expected: boolean;
    expectedAmount: number;
    completedAt?: string;
    releaseId?: string;
    refundId?: string;
  };
  paymentsSide: {
    cached: boolean;
    success?: boolean;
    releaseId?: string;
    refundId?: string;
    failureMessage?: string;
  };
  reconciliationStatus: ReconciliationStatus;
}

export interface AdminPayoutStatus {
  breakdown: SettlementBreakdown;
  operations: PayoutOperation[];
  reconciliationStatus: ReconciliationStatus;
}

export interface ReconciliationMismatch {
  enrollmentId: string;
  operationKey: string;
  expectedAmount: number;
  observedAmount: number;
  reason: string;
}

export interface ReconciliationRunResult {
  checkType: 'RELEASE_RECON' | 'REFUND_RECON';
  runId: string;
  startedAt: string;
  completedAt: string;
  enrollmentsScanned: number;
  expectedTotal: number;
  observedTotal: number;
  mismatches: ReconciliationMismatch[];
  status: 'ok' | 'mismatch' | 'error';
  errorMessage?: string;
}

export function getTeacherSettlement(): Promise<TeacherSettlement> {
  return apiGet('/api/v1/teachers/me/settlement');
}

export function getAdminPayoutStatus(enrollmentId: string): Promise<AdminPayoutStatus> {
  return apiGet(`/api/v1/admin/enrollments/${enrollmentId}/payout-status`);
}

export function runReleaseReconciliation(): Promise<ReconciliationRunResult> {
  return apiPost('/api/v1/admin/reconciliation/release/run', {});
}

export function runRefundReconciliation(): Promise<ReconciliationRunResult> {
  return apiPost('/api/v1/admin/reconciliation/refund/run', {});
}

export function finalizeSettlement(enrollmentId: string): Promise<{ summary: unknown }> {
  return apiPost(`/api/v1/admin/enrollments/${enrollmentId}/finalize-settlement`, {});
}
